import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ExpenseService } from '../../services/expense.service';
import { OrderService } from '../../services/order.service';
import { PaymentMethod } from '../../interfaces/expense.interface';

@Component({
    selector: 'app-deposit-form',
    templateUrl: './deposit-form.component.html',
    styleUrls: ['./deposit-form.component.scss']
})
export class DepositFormComponent implements OnInit, OnDestroy {
    depositForm: FormGroup;
    paymentMethods: PaymentMethod[] = [];
    serviceId!: number;
    submitting = false;
    submitted = false;
    evidenceFileName = '';
    evidenceUrl = '';
    isAdditionalDeposit = false;
    serviceDeposits: any[] = [];
    loadingDeposits = false;
    serviceFolio = '';

    // Edición inline de un depósito existente, directamente en su fila del historial.
    // El formulario superior ("Nuevo depósito") nunca se reutiliza para editar.
    editForm: FormGroup;
    editingDeposit: any = null;
    editSubmitting = false;
    editSubmitted = false;
    editEvidenceFileName = '';
    editEvidenceUrl = '';

    private uploadedPath = '';
    private editUploadedPath = '';
    private formSaved = false;
    private returnPage = 1;
    private returnScrollTop = 0;

    constructor(
        private fb: FormBuilder,
        private route: ActivatedRoute,
        private router: Router,
        private expenseService: ExpenseService,
        private orderService: OrderService
    ) {
        this.depositForm = this.fb.group({
            mileage: ['', [Validators.min(0)]],
            amount: ['', [Validators.required, Validators.min(0.01)]],
            payment_method_id: ['', Validators.required],
            evidence: ['']
        });
        this.editForm = this.fb.group({
            mileage: ['', [Validators.min(0)]],
            amount: ['', [Validators.required, Validators.min(0.01)]],
            payment_method_id: ['', Validators.required],
            evidence: ['']
        });
    }

    ngOnInit(): void {
        const id = this.route.snapshot.paramMap.get('id');
        if (id) this.serviceId = parseInt(id, 10);
        this.isAdditionalDeposit = this.route.snapshot.queryParamMap.get('from') === 'expense';
        const returnPage = this.route.snapshot.queryParamMap.get('returnPage');
        if (returnPage) this.returnPage = parseInt(returnPage, 10);
        const scrollTop = this.route.snapshot.queryParamMap.get('scrollTop');
        if (scrollTop) this.returnScrollTop = parseInt(scrollTop, 10);

        if (this.isAdditionalDeposit) {
            this.depositForm.get('mileage')!.clearValidators();
            this.depositForm.get('mileage')!.updateValueAndValidity();
        } else {
            this.depositForm.get('mileage')!.setValidators([Validators.required, Validators.min(0)]);
            this.depositForm.get('mileage')!.updateValueAndValidity();
        }

        this.expenseService.getPaymentMethods().subscribe({
            next: (data) => this.paymentMethods = data,
            error: () => {
                this.paymentMethods = [
                    { id: 1, name: 'Efectivo' },
                    { id: 2, name: 'Transferencia' },
                    { id: 3, name: 'Tarjeta de crédito' },
                    { id: 4, name: 'Tarjeta de débito' }
                ];
            }
        });

        if (this.serviceId) {
            this.loadServiceDeposits();
            this.orderService.getId(this.serviceId).subscribe({
                next: (service: any) => { this.serviceFolio = service?.folio ?? ''; },
                error: () => {}
            });
        }
    }

    private loadServiceDeposits(): void {
        this.loadingDeposits = true;
        this.expenseService.getDepositsList({ service_id: this.serviceId }).subscribe({
            next: (resp: any) => {
                this.serviceDeposits = Array.isArray(resp?.data) ? resp.data : (Array.isArray(resp) ? resp : []);
                this.loadingDeposits = false;
            },
            error: () => { this.loadingDeposits = false; }
        });
    }

    get depositTitle(): string {
        const count = this.serviceDeposits[0]?.deposits_count ?? 0;
        const folio = this.serviceFolio ? ` - Folio ${this.serviceFolio}` : '';
        if (count === 0) return `Depósito inicial${folio}`;
        else return `Realizar depósito${folio}`;
    }

    get depositButtonLabel(): string {
        const count = this.serviceDeposits[0]?.deposits_count ?? 0;
        return count === 0 ? 'Depósito inicial' : 'Realizar depósito';
    }

    get depositHistory(): any[] {
        const deposits: any[] = this.serviceDeposits[0]?.deposit || [];
        return [...deposits]
            .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
            .map((d, i) => ({
                ...d,
                label: i === 0 ? 'Depósito inicial' : `Depósito #${i + 1}`,
                isInitial: i === 0,
            }));
    }

    get f() { return this.depositForm.controls; }
    get ef() { return this.editForm.controls; }

    trackByDepositId(_index: number, deposit: any): number {
        return deposit.id;
    }

    onFileChange(event: any): void {
        const file: File = event.target.files[0];
        if (!file) return;
        this.evidenceFileName = file.name;

        this.expenseService.uploadFile(file, 'deposit', 0).subscribe({
            next: (res: any) => {
                if (this.uploadedPath) this.expenseService.deleteFile(this.uploadedPath).subscribe();
                this.uploadedPath = res?.path ?? '';
                this.evidenceUrl = res?.url ?? '';
                this.depositForm.patchValue({ evidence: this.evidenceUrl });
            },
            error: () => {}
        });
    }

    triggerFileInput(): void {
        (document.getElementById('evidence-file') as HTMLInputElement)?.click();
    }

    onSubmit(): void {
        this.submitted = true;
        if (this.depositForm.invalid) return;

        this.submitting = true;
        const { mileage, amount, payment_method_id, evidence } = this.depositForm.value;

        this.expenseService.createDeposit({
            service_id: this.serviceId,
            mileage: mileage !== '' && mileage !== null ? +mileage : undefined,
            amount: +amount,
            payment_method_id: +payment_method_id,
            evidence: evidence || undefined
        }).subscribe({
            next: () => {
                this.formSaved = true;
                this.router.navigate(['/service/deposits'], { queryParams: { expandService: this.serviceId, returnPage: this.returnPage, scrollTop: this.returnScrollTop } });
            },
            error: () => { this.submitting = false; }
        });
    }

    onCancel(): void {
        this.router.navigate(['/service/deposits']);
    }

    // ── Edición inline de un depósito existente ─────────────────────────────

    toggleEditDeposit(deposit: any): void {
        if (this.editingDeposit?.id === deposit.id) {
            this.closeEditPanel();
            return;
        }
        this.deleteEditUploadedFileIfUnsaved();
        this.editingDeposit = deposit;
        this.editSubmitted = false;
        this.editEvidenceFileName = deposit.evidence ? 'Comprobante actual' : '';
        this.editEvidenceUrl = deposit.evidence || '';
        this.editForm.reset();
        this.editForm.patchValue({
            mileage: deposit.mileage ?? '',
            amount: deposit.amount,
            payment_method_id: deposit.payment_method_id,
            evidence: deposit.evidence || ''
        });
        if (deposit.isInitial) {
            this.editForm.get('mileage')!.setValidators([Validators.required, Validators.min(0)]);
        } else {
            this.editForm.get('mileage')!.clearValidators();
        }
        this.editForm.get('mileage')!.updateValueAndValidity();
    }

    closeEditPanel(): void {
        this.deleteEditUploadedFileIfUnsaved();
        this.editingDeposit = null;
        this.editSubmitting = false;
        this.editSubmitted = false;
        this.editEvidenceFileName = '';
        this.editEvidenceUrl = '';
        this.editUploadedPath = '';
        this.editForm.reset();
    }

    onEditFileChange(event: any): void {
        const file: File = event.target.files[0];
        if (!file) return;
        this.editEvidenceFileName = file.name;

        this.expenseService.uploadFile(file, 'deposit', 0).subscribe({
            next: (res: any) => {
                if (this.editUploadedPath) this.expenseService.deleteFile(this.editUploadedPath).subscribe();
                this.editUploadedPath = res?.path ?? '';
                this.editEvidenceUrl = res?.url ?? '';
                this.editForm.patchValue({ evidence: this.editEvidenceUrl });
            },
            error: () => {}
        });
    }

    triggerEditFileInput(): void {
        (document.getElementById('edit-evidence-file') as HTMLInputElement)?.click();
    }

    submitEdit(): void {
        this.editSubmitted = true;
        if (this.editForm.invalid || !this.editingDeposit) return;

        this.editSubmitting = true;
        const { mileage, amount, payment_method_id, evidence } = this.editForm.value;
        this.expenseService.updateDeposit(this.editingDeposit.id, {
            service_id: this.serviceId,
            mileage: mileage !== '' && mileage !== null ? +mileage : undefined,
            amount: +amount,
            payment_method_id: +payment_method_id,
            evidence: evidence || undefined
        }).subscribe({
            next: () => {
                this.editUploadedPath = '';
                this.editingDeposit = null;
                this.editSubmitting = false;
                this.loadServiceDeposits();
            },
            error: () => { this.editSubmitting = false; }
        });
    }

    private deleteEditUploadedFileIfUnsaved(): void {
        if (this.editUploadedPath) {
            this.expenseService.deleteFile(this.editUploadedPath).subscribe();
            this.editUploadedPath = '';
        }
    }

    @HostListener('window:beforeunload')
    onBeforeUnload(): void {
        this.deleteUploadedFileIfUnsaved();
        this.deleteEditUploadedFileIfUnsaved();
    }

    ngOnDestroy(): void {
        this.deleteUploadedFileIfUnsaved();
        this.deleteEditUploadedFileIfUnsaved();
    }

    private deleteUploadedFileIfUnsaved(): void {
        if (!this.formSaved && this.uploadedPath) {
            this.expenseService.deleteFile(this.uploadedPath).subscribe();
        }
    }
}
